import React, { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  Grid,
  Button,
  TextField,
  makeStyles,
} from '@material-ui/core';

import Loading from '@common/components/Loading';
import { GENERIC_INVALID_CREDENTIALS_MSG } from '@common/utilities/text-constants';

import Api from '../api';

const useStyles = makeStyles((theme) => ({
  container: {
    height: '100vh',
    backgroundImage: 'url("https://s3.amazonaws.com/bright.public/TTEC+Background+Login+Page.png")',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'top center',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '50px',
  },
  logo: {
    marginTop: '-50px',
    width: 250,
    [theme.breakpoints.down('sm')]: {
      width: 210,
    },
  },
  loginContainer: {
    width: 400,
  },
  loginTextContainer: {
    background: 'white',
    width: '100%',
    textAlign: 'center',
    borderRadius: '7px',
    fontFamily: "'Public Sans',sans-serif",
    fontSize: '18px',
  },
  textFieldContainer: {
    width: '540px',
    padding: '10px !important',
    [theme.breakpoints.down('md')]: {
      width: '460px',
    },
    [theme.breakpoints.down('sm')]: {
      width: '360px',
    },
  },
  loginButtonContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '4px !important',
  },
  loginButton: {
    height: '40px',
    width: '128px',
    background: 'transparent',
    textTransform: 'none',
    padding: '12px 10px 14px 10px',
    color: 'white',
    fontWeight: 'bold',
    cursor: 'pointer !important',
    fontSize: '24px',
    fontFamily: "'Public Sans',sans-serif",

    '&:hover': {
      background: 'rgba(255, 255, 255, 0.2)',
      cursor: 'pointer !important',
      border: '1px solid white',
    },
  },
  header: {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100px',
    position: 'fixed',
    top: '0',
  },
}));

const LoginTTEC = ({ userStore }) => {
  const classes = useStyles();

  const [isLoading, setIsLoading] = useState(true);
  const [email, setEmail] = useState('');

  useEffect(() => {
    userStore.fetchUserProfile().then(() => {
      setIsLoading(false);
    });
  }, [userStore]);

  const renderLoading = () => <Loading />;

  const handleLogin = async () => {
    try {
      const response = await Api.getLoginMechanism(email.trim());

      if (response?.status !== 200) {
        alert(GENERIC_INVALID_CREDENTIALS_MSG);
      }

      if (response.data.next) {
        window.location.href = response.data.next;
      } else if (response.data) {
        userStore.fetchUserProfile();
      }
    } catch (e) {
      alert('An error occurred. Please try again.');
    }
  };

  const keyPress = (e) => {
    if (e.keyCode === 13) {
      handleLogin();
    }
  };

  const renderLoginButtons = () => {
    if (userStore.ssoUrl || isLoading) {
      return renderLoading();
    }

    return (
      <div className={classes.container}>
        <img
          alt="logo"
          className={classes.logo}
          src="https://s3.amazonaws.com/bright.public/TTEC+logo_white.png"
        />
        <Grid
          container
          item
          direction="column"
          justify="center"
          alignContent="center"
          spacing={4}
          className={classes.loginContainer}
        >
          <Grid item className={classes.textFieldContainer}>
            <TextField
              className={classes.loginTextContainer}
              fullWidth
              onKeyDown={keyPress}
              onChange={(e) => setEmail(e.target.value)}
              id="outlined-password-input"
              placeholder="Enter your Email"
              autoComplete="current-password"
              inputProps={{
                className: classes.loginTextContainer,
              }}
              variant="outlined"
            />
          </Grid>
          <Grid item className={classes.loginButtonContainer}>
            <Button
              className={classes.loginButton}
              type="submit"
              onClick={handleLogin}
            >
              Login
            </Button>
          </Grid>
        </Grid>
      </div>
    );
  };

  return (userStore.isAuthenticated ? <Redirect to="/" /> : renderLoginButtons());
};

LoginTTEC.propTypes = {
  classes: PropTypes.shape({
    container: PropTypes.string.isRequired,
    loginContainerWrapper: PropTypes.string.isRequired,
    logo: PropTypes.string.isRequired,
    loginContainer: PropTypes.string.isRequired,
    loginTextContainer: PropTypes.string.isRequired,
    textFieldContainer: PropTypes.string.isRequired,
    loginButtonContainer: PropTypes.string.isRequired,
    loginButton: PropTypes.string.isRequired,
    header: PropTypes.string.isRequired,
  }).isRequired,
  userStore: PropTypes.shape({
    isAuthenticated: PropTypes.bool.isRequired,
    ssoUrl: PropTypes.string,
    fetchUserProfile: PropTypes.func.isRequired,
  }).isRequired,
};

export default LoginTTEC;
